// --- Employee newsletter ---------------------------------------------------------
// Hand-written client because api.ts is generated from a running backend; after
// the backend ships, `npm run generate-api` produces a typed NewsletterApi that
// can replace this file. Types mirror onestaff-backend/src/newsletter/newsletter-blocks.ts.
import { realAxios } from './index';

export type NewsletterBlockType =
  | 'header'
  | 'heading'
  | 'text'
  | 'image'
  | 'button'
  | 'divider'
  | 'spacer'
  | 'columns'
  | 'quote';

export type BlockAlign = 'left' | 'center' | 'right';

interface BaseBlock {
  id: string;
  type: NewsletterBlockType;
}
export interface HeaderBlock extends BaseBlock {
  type: 'header';
  title: string;
  subtitle?: string;
  showLogo: boolean;
  backgroundColor?: string;
  textColor?: string;
  align?: BlockAlign;
}
export interface HeadingBlock extends BaseBlock {
  type: 'heading';
  text: string;
  level: 1 | 2 | 3;
  align?: BlockAlign;
  color?: string;
}
export interface TextBlock extends BaseBlock {
  type: 'text';
  html: string;
  align?: BlockAlign;
}
export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt?: string;
  href?: string;
  width?: number;
  align?: BlockAlign;
  caption?: string;
}
export interface ButtonBlock extends BaseBlock {
  type: 'button';
  label: string;
  url: string;
  align?: BlockAlign;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  fullWidth?: boolean;
}
export interface DividerBlock extends BaseBlock {
  type: 'divider';
  color?: string;
  thickness?: number;
}
export interface SpacerBlock extends BaseBlock {
  type: 'spacer';
  height: number;
}
export interface ColumnsBlock extends BaseBlock {
  type: 'columns';
  columns: Array<{ html: string; imageSrc?: string; imageHref?: string }>;
}
export interface QuoteBlock extends BaseBlock {
  type: 'quote';
  text: string;
  author?: string;
  accentColor?: string;
}
export type NewsletterBlock =
  | HeaderBlock
  | HeadingBlock
  | TextBlock
  | ImageBlock
  | ButtonBlock
  | DividerBlock
  | SpacerBlock
  | ColumnsBlock
  | QuoteBlock;

export interface NewsletterDesign {
  backgroundColor: string;
  contentBackgroundColor: string;
  textColor: string;
  primaryColor: string;
  fontFamily: string;
  logoUrl?: string;
  footerText?: string;
}

export const DEFAULT_DESIGN: NewsletterDesign = {
  backgroundColor: '#f4f5f7',
  contentBackgroundColor: '#ffffff',
  textColor: '#333333',
  primaryColor: '#322e70',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

export type AudienceMode = 'ALL' | 'FILTER' | 'MANUAL';
export interface NewsletterAudience {
  mode: AudienceMode;
  seasonId?: number;
  departmentIds?: number[];
  workPositionIds?: number[];
  roles?: string[];
  employeeIds?: number[];
  excludeEmployeeIds?: number[];
  includeTerminated?: boolean;
}

export type EditionStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'CANCELLED'
  | 'FAILED';
export type RecipientStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface EngagementTotals {
  totalRecipients: number;
  totalSent: number;
  uniqueOpens: number;
  uniqueClicks: number;
  openRate: number;
  clickRate: number;
}

export interface EditionSummary extends Partial<EngagementTotals> {
  id: number;
  newsletterId: number;
  number: number;
  name: string;
  subject: string;
  preheader: string | null;
  status: EditionStatus;
  scheduledAt: string | null;
  sendingStartedAt: string | null;
  sentAt: string | null;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  newsletter?: { id: number; name: string };
}

export interface NewsletterEntity extends EngagementTotals {
  id: number;
  name: string;
  description: string | null;
  fromName: string | null;
  fromEmail: string | null;
  replyTo: string | null;
  design: Partial<NewsletterDesign> | null;
  audience: NewsletterAudience | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: number; firstName: string; lastName: string } | null;
  editionCount: number;
  sentEditionCount: number;
  draftEditionCount: number;
  lastSentAt: string | null;
  nextScheduledAt: string | null;
}

export interface NewsletterDetail
  extends Omit<
    NewsletterEntity,
    | 'editionCount'
    | 'sentEditionCount'
    | 'draftEditionCount'
    | 'lastSentAt'
    | 'nextScheduledAt'
  > {
  editions: EditionSummary[];
}

export interface EditionEntity extends EditionSummary {
  blocks: NewsletterBlock[];
  design: Partial<NewsletterDesign> | null;
  audience: NewsletterAudience | null;
  newsletter: {
    id: number;
    name: string;
    fromName: string | null;
    fromEmail: string | null;
    replyTo: string | null;
  };
  createdBy?: { id: number; firstName: string; lastName: string } | null;
}

export interface AudienceMember {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  departments: string[];
}

export interface NewsletterSettings {
  id: number;
  useCustomSmtp: boolean;
  smtpHost: string | null;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string | null;
  hasPassword: boolean;
  fromName: string | null;
  fromEmail: string | null;
  replyTo: string | null;
  footerText: string | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  platformFromEmail: string;
}

export interface LinkStat {
  linkId: string;
  url: string;
  label: string;
  blockId: string;
  blockType: string;
  clicks: number;
  uniqueClicks: number;
}

export interface EditionStats extends EngagementTotals {
  edition: EditionEntity;
  failed: number;
  pending: number;
  totalOpens: number;
  totalClicks: number;
  clickToOpenRate: number;
  links: LinkStat[];
  timeline: Array<{ time: string; opens: number; clicks: number }>;
  devices: { mobile: number; desktop: number; unknown: number };
}

export interface RecipientRow {
  id: number;
  employeeId: number | null;
  email: string;
  firstName: string;
  lastName: string;
  status: RecipientStatus;
  sentAt: string | null;
  error: string | null;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  openCount: number;
  firstClickedAt: string | null;
  lastClickedAt: string | null;
  clickCount: number;
}

export interface NewsletterOverview extends EngagementTotals {
  newsletters: number;
  editionsSent: number;
  recent: EditionSummary[];
  upcoming: EditionSummary[];
  drafts: EditionSummary[];
}

export interface RenderedPreview {
  subject: string;
  html: string;
  text: string;
  links?: Array<{ linkId: string; url: string; label: string }>;
}

export const newsletterApi = {
  // settings
  getSettings: async () =>
    realAxios.get<NewsletterSettings>('newsletter/settings'),
  updateSettings: async (
    dto: Partial<NewsletterSettings> & { smtpPassword?: string },
  ) => realAxios.put<NewsletterSettings>('newsletter/settings', dto),
  testSettings: async (to: string) =>
    realAxios.post<{ ok: boolean; error?: string }>(
      'newsletter/settings/test',
      { to },
    ),

  // overview
  overview: async () =>
    realAxios.get<NewsletterOverview>('newsletter/overview'),

  // newsletters
  findAll: async (includeArchived = false) =>
    realAxios.get<NewsletterEntity[]>('newsletter', {
      params: includeArchived ? { includeArchived: 'true' } : {},
    }),
  create: async (dto: Partial<NewsletterEntity>) =>
    realAxios.post<NewsletterEntity>('newsletter', dto),
  findOne: async (id: number) =>
    realAxios.get<NewsletterDetail>(`newsletter/${id}`),
  update: async (id: number, dto: Partial<NewsletterEntity>) =>
    realAxios.patch<NewsletterEntity>(`newsletter/${id}`, dto),
  remove: async (id: number) => realAxios.delete(`newsletter/${id}`),

  // editions
  createEdition: async (
    newsletterId: number,
    dto: { name?: string; duplicateFromEditionId?: number },
  ) =>
    realAxios.post<EditionEntity>(`newsletter/${newsletterId}/editions`, dto),
  findEdition: async (editionId: number) =>
    realAxios.get<EditionEntity>(`newsletter/editions/${editionId}`),
  updateEdition: async (
    editionId: number,
    dto: {
      name?: string;
      subject?: string;
      preheader?: string;
      blocks?: NewsletterBlock[];
      design?: Partial<NewsletterDesign>;
      audience?: NewsletterAudience;
    },
  ) => realAxios.patch<EditionEntity>(`newsletter/editions/${editionId}`, dto),
  removeEdition: async (editionId: number) =>
    realAxios.delete(`newsletter/editions/${editionId}`),
  previewEdition: async (editionId: number) =>
    realAxios.get<RenderedPreview>(`newsletter/editions/${editionId}/preview`),
  previewDraft: async (body: {
    blocks: NewsletterBlock[];
    design?: Partial<NewsletterDesign>;
    subject?: string;
    preheader?: string;
  }) => realAxios.post<RenderedPreview>('newsletter/preview', body),
  sendTest: async (editionId: number, emails: string[]) =>
    realAxios.post<{ sent: number }>(`newsletter/editions/${editionId}/test`, {
      emails,
    }),
  schedule: async (editionId: number, scheduledAt?: string) =>
    realAxios.post<EditionSummary>(
      `newsletter/editions/${editionId}/schedule`,
      scheduledAt ? { scheduledAt } : {},
    ),
  cancel: async (editionId: number) =>
    realAxios.post<EditionSummary>(`newsletter/editions/${editionId}/cancel`),
  stats: async (editionId: number) =>
    realAxios.get<EditionStats>(`newsletter/editions/${editionId}/stats`),
  recipients: async (
    editionId: number,
    params?: { filter?: string; search?: string },
  ) =>
    realAxios.get<RecipientRow[]>(
      `newsletter/editions/${editionId}/recipients`,
      { params },
    ),

  // audience & assets
  previewAudience: async (audience: NewsletterAudience) =>
    realAxios.post<{ count: number; members: AudienceMember[] }>(
      'newsletter/audience/preview',
      { audience },
    ),
  uploadAsset: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return realAxios.post<{ url: string }>('newsletter/assets', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
